import {inches} from "../../const";
import {SerGlass} from "../../../../../../api/elevationTool/shared";
import type {MdFrameItem} from "../frame/MdFrameBlock";
import type {MdDoorLite} from "../door/MdDoorLite";

// the glass make-up: a single value, a lite is one or the other.
export type MdGlassType = "tempered" | "laminate";

export const glassTypeLabels: Record<MdGlassType, string> = {
    "tempered": "Tempered",
    "laminate": "Laminate",
};

// how the glass is held in. The three labels are the `doorLiteKit` values in
// ddl_for_openings; the backend maps a door lite's bead to that row on save.
export type MdGlassBead = "thermal" | "screwIn" | "snapIn";

export const glassBeadLabels: Record<MdGlassBead, string> = {
    "thermal": "Thermal",
    "screwIn": "Screw-in",
    "snapIn": "Snap-in",
};

// how a lite is glazed. Every field is optional; an omitted field reads as its
// default (see resolveGlass). thickness / undersize are plain drawing-unit numbers —
// they take no part in the dimension solver, so they are not FrmVars.
export type MdGlass = {
    // a sealed thermal unit rather than a single pane.
    thermal?: boolean;
    type?: MdGlassType;
    // the thickness of the pane, or of the sealed unit when thermal.
    thickness?: number;
    bead?: MdGlassBead;
    // how much smaller the glass is than the cut-out: a total per axis, not per edge.
    undersize?: number;
};

export type MdGlassResolved = Required<MdGlass>;

export const defaultGlassThickness = 1/4 * inches;
export const thermalGlassThickness = 1 * inches;

export const defaultGlass: MdGlassResolved = {
    thermal: false,
    type: "tempered",
    thickness: defaultGlassThickness,
    bead: "snapIn",
    undersize: 1/8 * inches,
};

// the bead an unset bead reads as: a thermal unit takes the thermal bead.
export function defaultBead(thermal: boolean): MdGlassBead {
    return thermal ? "thermal" : defaultGlass.bead;
}

export function resolveGlass(glass: MdGlass | undefined): MdGlassResolved {
    const thermal = glass?.thermal ?? defaultGlass.thermal;
    return {
        thermal,
        type: glass?.type ?? defaultGlass.type,
        thickness: glass?.thickness ?? defaultGlass.thickness,
        bead: glass?.bead ?? defaultBead(thermal),
        undersize: glass?.undersize ?? defaultGlass.undersize,
    };
}

export function liteGlass(item: MdFrameItem): MdGlassResolved {
    return resolveGlass(item.kind === "lite" ? item.glass : undefined);
}

export function doorLiteGlass(lite: MdDoorLite): MdGlassResolved {
    return resolveGlass(lite.glass);
}

// the glass size for a cut-out. The bead does not change it.
export function glassSize(cutOutWidth: number, cutOutHeight: number, glass: MdGlassResolved): {width: number; height: number} {
    return {
        width: cutOutWidth - glass.undersize,
        height: cutOutHeight - glass.undersize,
    };
}

// apply a user edit. Setting thermal also selects the thermal bead and raises a
// thickness still on the non-thermal default to the thermal default; a thickness the
// user set is kept. Clearing thermal keeps the thickness and drops a thermal bead back
// to the default.
export function withGlassEdit(glass: MdGlass | undefined, patch: MdGlass): MdGlass {
    const next: MdGlass = {...glass, ...patch};
    const wasThermal = glass?.thermal ?? defaultGlass.thermal;

    if(patch.thermal !== undefined && patch.thermal !== wasThermal) {
        if(patch.thermal) {
            next.bead = "thermal";
            if(next.thickness === undefined || sameSize(next.thickness, defaultGlassThickness)) {
                next.thickness = thermalGlassThickness;
            }
        } else if(next.bead === "thermal") {
            next.bead = undefined;
        }
    }

    return next;
}

// a thermal unit held by a non-thermal bead. Allowed, but the ribbon warns.
export function beadConflictsWithThermal(glass: MdGlassResolved): boolean {
    return glass.thermal && glass.bead !== "thermal";
}

function sameSize(a: number, b: number): boolean {
    return Math.abs(a - b) < 1e-6;
}

// serialize: sizes in inches, every field on its default omitted, undefined when
// nothing is left.
export function serGlass(glass: MdGlass | undefined): SerGlass | undefined {
    if(!glass) return undefined;

    const out: SerGlass = {};
    if(glass.thermal !== undefined && glass.thermal !== defaultGlass.thermal) out.thermal = glass.thermal;
    if(glass.type !== undefined && glass.type !== defaultGlass.type) out.type = glass.type;
    if(glass.thickness !== undefined && !sameSize(glass.thickness, defaultGlass.thickness)) out.thicknessIn = glass.thickness / inches;
    if(glass.bead !== undefined && glass.bead !== defaultBead(glass.thermal ?? defaultGlass.thermal)) out.bead = glass.bead;
    if(glass.undersize !== undefined && !sameSize(glass.undersize, defaultGlass.undersize)) out.undersizeIn = glass.undersize / inches;

    return Object.keys(out).length > 0 ? out : undefined;
}

export function deGlass(s: SerGlass | undefined): MdGlass | undefined {
    if(!s) return undefined;

    const out: MdGlass = {};
    if(s.thermal !== undefined) out.thermal = s.thermal;
    if(s.type !== undefined) out.type = s.type;
    if(s.thicknessIn !== undefined) out.thickness = s.thicknessIn * inches;
    if(s.bead !== undefined) out.bead = s.bead;
    if(s.undersizeIn !== undefined) out.undersize = s.undersizeIn * inches;

    return Object.keys(out).length > 0 ? out : undefined;
}
